import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Typography,
    Paper,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TextField,
    CircularProgress,
    Alert,
    Button,
    Pagination,
    Switch,
    FormControlLabel,
    Chip,
    Tabs,
    Tab,
    Box,
    Stack,
    Snackbar
} from "@mui/material";
import MarkEmailReadRoundedIcon from "@mui/icons-material/MarkEmailReadRounded";
import styled from "styled-components";
import dayjs from "dayjs";
import { functions } from "../services/firebase";
import { captureError } from "../lib/errorReporting";
import { useIsMobile } from "../hooks/useIsMobile";
import { THEME_MAP } from "../theme";

const PAGE_SIZE = 25;

const PageContainer = styled.div`
    padding: 24px;
    max-width: 1100px;
    margin: 0 auto;
`;

const Header = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 8px;

    svg {
        color: ${THEME_MAP["color-primary-500"]};
    }
`;

const Subtle = styled(Typography)`
    color: ${THEME_MAP["color-basic-600"]};
`;

const statusColor = (status) => {
    switch (status) {
        case "sent":
            return "success";
        case "failed":
            return "error";
        case "test":
            return "info";
        case "suppressed":
            return "warning";
        default:
            return "default";
    }
};

function a11yProps(index) {
    return { id: `email-tab-${index}`, "aria-controls": `email-tabpanel-${index}` };
}

function TabPanel({ children, value, index }) {
    if (value !== index) return null;
    return (
        <Box role="tabpanel" sx={{ pt: 3 }} {...a11yProps(index)}>
            {children}
        </Box>
    );
}

export function EmailSystem() {
    const getSystem = useMemo(() => functions.httpsCallable("getOwnerEmailSystem"), []);
    const updateRule = useMemo(() => functions.httpsCallable("updateOwnerEmailRule"), []);
    const updateTemplate = useMemo(() => functions.httpsCallable("updateOwnerEmailTemplate"), []);
    const listDispatches = useMemo(() => functions.httpsCallable("listOwnerEmailDispatches"), []);
    const sendTest = useMemo(() => functions.httpsCallable("sendOwnerEmailTest"), []);
    const isMobile = useIsMobile();

    const [tab, setTab] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [toast, setToast] = useState("");

    const [rules, setRules] = useState([]);
    const [templates, setTemplates] = useState([]);

    // Templates editing state keyed by slug.
    const [drafts, setDrafts] = useState({});
    const [savingTemplate, setSavingTemplate] = useState("");
    const [testEmail, setTestEmail] = useState({});
    const [sendingTest, setSendingTest] = useState("");

    // Activity log state.
    const [dispatches, setDispatches] = useState([]);
    const [dispatchTotal, setDispatchTotal] = useState(0);
    const [dispatchPage, setDispatchPage] = useState(1);
    const [dispatchLoading, setDispatchLoading] = useState(false);

    const loadSystem = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const res = await getSystem();
            const data = res?.data || {};
            setRules(data.rules || []);
            setTemplates(data.templates || []);
            const nextDrafts = {};
            (data.templates || []).forEach((t) => {
                nextDrafts[t.slug] = {
                    subject: t.subject || "",
                    preheader: t.preheader || "",
                    from_address: t.from_address || "",
                    reply_to: t.reply_to || "",
                    markdown_body: t.markdown_body || "",
                    enabled: Boolean(t.enabled)
                };
            });
            setDrafts(nextDrafts);
        } catch (err) {
            captureError(err, { view: "EmailSystem", action: "loadSystem" });
            setError(err?.message || "Failed to load the email system.");
        } finally {
            setLoading(false);
        }
    }, [getSystem]);

    useEffect(() => {
        loadSystem();
    }, [loadSystem]);

    const loadDispatches = useCallback(
        async (page) => {
            setDispatchLoading(true);
            try {
                const res = await listDispatches({ page, pageSize: PAGE_SIZE });
                const data = res?.data || {};
                setDispatches(data.rows || []);
                setDispatchTotal(Number.isFinite(data.total) ? data.total : 0);
            } catch (err) {
                captureError(err, { view: "EmailSystem", action: "loadDispatches" });
                setError(err?.message || "Failed to load the activity log.");
            } finally {
                setDispatchLoading(false);
            }
        },
        [listDispatches]
    );

    useEffect(() => {
        if (tab === 2) {
            loadDispatches(dispatchPage);
        }
    }, [tab, dispatchPage, loadDispatches]);

    // ── Rules ───────────────────────────────────────────────────────────────
    const handleRuleToggle = async (rule, enabled) => {
        setError("");
        try {
            const res = await updateRule({ slug: rule.slug, enabled });
            setRules((prev) => prev.map((r) => (r.slug === rule.slug ? res?.data?.rule || r : r)));
        } catch (err) {
            captureError(err, { view: "EmailSystem", action: "updateRule" });
            setError(err?.message || "Failed to update the rule.");
        }
    };

    const handleCooldownBlur = async (rule, value) => {
        const days = Number(value);
        if (!Number.isFinite(days) || days === rule.cooldown_days) return;
        try {
            const res = await updateRule({ slug: rule.slug, cooldown_days: days });
            setRules((prev) => prev.map((r) => (r.slug === rule.slug ? res?.data?.rule || r : r)));
            setToast("Cooldown updated.");
        } catch (err) {
            captureError(err, { view: "EmailSystem", action: "updateRuleCooldown" });
            setError(err?.message || "Failed to update the cooldown.");
        }
    };

    // ── Templates ───────────────────────────────────────────────────────────
    const setDraftField = (slug, field, value) => {
        setDrafts((prev) => ({ ...prev, [slug]: { ...prev[slug], [field]: value } }));
    };

    const handleSaveTemplate = async (slug) => {
        const draft = drafts[slug];
        if (!draft) return;
        setSavingTemplate(slug);
        setError("");
        try {
            const res = await updateTemplate({ slug, ...draft });
            const updated = res?.data?.template;
            if (updated) {
                setTemplates((prev) => prev.map((t) => (t.slug === slug ? updated : t)));
            }
            setToast("Template saved.");
        } catch (err) {
            captureError(err, { view: "EmailSystem", action: "updateTemplate" });
            setError(err?.message || "Failed to save the template.");
        } finally {
            setSavingTemplate("");
        }
    };

    const handleSendTest = async (slug) => {
        const to = (testEmail[slug] || "").trim();
        if (!to) return;
        setSendingTest(slug);
        setError("");
        try {
            await sendTest({ templateSlug: slug, toEmail: to });
            setToast(`Test sent to ${to}.`);
        } catch (err) {
            captureError(err, { view: "EmailSystem", action: "sendTest" });
            setError(err?.message || "Failed to send the test email.");
        } finally {
            setSendingTest("");
        }
    };

    const formatDate = (value) => {
        if (!value) return "";
        const parsed = value?.toDate ? dayjs(value.toDate()) : dayjs(value);
        return parsed.isValid() ? parsed.format("MMM D, YYYY h:mm A") : "";
    };

    const dispatchPageCount = Math.max(1, Math.ceil((dispatchTotal || 0) / PAGE_SIZE));

    if (loading) {
        return (
            <PageContainer>
                <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                    <CircularProgress />
                </Box>
            </PageContainer>
        );
    }

    return (
        <PageContainer>
            <Header>
                <MarkEmailReadRoundedIcon fontSize="large" />
                <div>
                    <Typography variant="h5" sx={{ fontWeight: 800 }}>
                        Email System
                    </Typography>
                    <Subtle variant="body2">
                        Lifecycle emails for bar owners. Sending is gated by the flags in System Config.
                    </Subtle>
                </div>
            </Header>

            {error ? (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
                    {error}
                </Alert>
            ) : null}

            <Tabs value={tab} onChange={(_e, v) => setTab(v)} sx={{ mb: 1 }}>
                <Tab label="Rules" {...a11yProps(0)} />
                <Tab label="Templates" {...a11yProps(1)} />
                <Tab label="Activity" {...a11yProps(2)} />
            </Tabs>

            {/* ── Rules ────────────────────────────────────────────────────── */}
            <TabPanel value={tab} index={0}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                    Rules
                </Typography>
                <Stack spacing={2}>
                    {rules.map((rule) => (
                        <Paper key={rule.slug} variant="outlined" sx={{ p: 2 }}>
                            <Stack
                                direction={isMobile ? "column" : "row"}
                                spacing={2}
                                alignItems={isMobile ? "flex-start" : "center"}
                                justifyContent="space-between"
                            >
                                <Box sx={{ flex: 1 }}>
                                    <Typography sx={{ fontWeight: 700 }}>{rule.name}</Typography>
                                    <Subtle variant="body2">{rule.description}</Subtle>
                                </Box>
                                <TextField
                                    label="Cooldown (days)"
                                    type="number"
                                    size="small"
                                    defaultValue={rule.cooldown_days}
                                    onBlur={(e) => handleCooldownBlur(rule, e.target.value)}
                                    sx={{ width: 140 }}
                                    disabled={rule.slug === "profile_setup_success"}
                                    helperText={
                                        rule.slug === "profile_setup_success" ? "Sent once per bar" : " "
                                    }
                                />
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={Boolean(rule.enabled)}
                                            onChange={(e) => handleRuleToggle(rule, e.target.checked)}
                                        />
                                    }
                                    label={rule.enabled ? "Enabled" : "Disabled"}
                                />
                            </Stack>
                        </Paper>
                    ))}
                </Stack>
            </TabPanel>

            {/* ── Templates ────────────────────────────────────────────────── */}
            <TabPanel value={tab} index={1}>
                <Stack spacing={3}>
                    {templates.map((template) => {
                        const draft = drafts[template.slug] || {};
                        return (
                            <Paper key={template.slug} variant="outlined" sx={{ p: 3 }}>
                                <Stack
                                    direction="row"
                                    alignItems="center"
                                    justifyContent="space-between"
                                    sx={{ mb: 2 }}
                                >
                                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                        {template.name}
                                    </Typography>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={Boolean(draft.enabled)}
                                                onChange={(e) =>
                                                    setDraftField(template.slug, "enabled", e.target.checked)
                                                }
                                            />
                                        }
                                        label="Active"
                                    />
                                </Stack>
                                <Stack spacing={2}>
                                    <TextField
                                        label="Subject"
                                        size="small"
                                        fullWidth
                                        value={draft.subject || ""}
                                        onChange={(e) =>
                                            setDraftField(template.slug, "subject", e.target.value)
                                        }
                                    />
                                    <TextField
                                        label="Preheader"
                                        size="small"
                                        fullWidth
                                        value={draft.preheader || ""}
                                        onChange={(e) =>
                                            setDraftField(template.slug, "preheader", e.target.value)
                                        }
                                    />
                                    <Stack direction={isMobile ? "column" : "row"} spacing={2}>
                                        <TextField
                                            label="From"
                                            size="small"
                                            fullWidth
                                            value={draft.from_address || ""}
                                            onChange={(e) =>
                                                setDraftField(template.slug, "from_address", e.target.value)
                                            }
                                        />
                                        <TextField
                                            label="Reply-to"
                                            size="small"
                                            fullWidth
                                            value={draft.reply_to || ""}
                                            onChange={(e) =>
                                                setDraftField(template.slug, "reply_to", e.target.value)
                                            }
                                        />
                                    </Stack>
                                    <TextField
                                        label="Body (markdown)"
                                        multiline
                                        minRows={8}
                                        fullWidth
                                        value={draft.markdown_body || ""}
                                        onChange={(e) =>
                                            setDraftField(template.slug, "markdown_body", e.target.value)
                                        }
                                        helperText="Supports # headings, **bold**, [links](https://…), and - lists. Variables: {{bar_name}}, {{dashboard_url}}."
                                    />
                                    <Stack
                                        direction={isMobile ? "column" : "row"}
                                        spacing={2}
                                        alignItems={isMobile ? "stretch" : "center"}
                                        justifyContent="space-between"
                                    >
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            onClick={() => handleSaveTemplate(template.slug)}
                                            disabled={savingTemplate === template.slug}
                                        >
                                            {savingTemplate === template.slug ? "Saving…" : "Save template"}
                                        </Button>
                                        <Stack
                                            direction={isMobile ? "column" : "row"}
                                            spacing={1}
                                            alignItems="stretch"
                                        >
                                            <TextField
                                                label="Send test to"
                                                size="small"
                                                value={testEmail[template.slug] || ""}
                                                onChange={(e) =>
                                                    setTestEmail((prev) => ({
                                                        ...prev,
                                                        [template.slug]: e.target.value
                                                    }))
                                                }
                                                sx={{ minWidth: 240 }}
                                            />
                                            <Button
                                                variant="outlined"
                                                onClick={() => handleSendTest(template.slug)}
                                                disabled={sendingTest === template.slug}
                                            >
                                                {sendingTest === template.slug ? "Sending…" : "Send test"}
                                            </Button>
                                        </Stack>
                                    </Stack>
                                </Stack>
                            </Paper>
                        );
                    })}
                </Stack>
            </TabPanel>

            {/* ── Activity ─────────────────────────────────────────────────── */}
            <TabPanel value={tab} index={2}>
                <Paper variant="outlined">
                    {dispatchLoading ? (
                        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                            <CircularProgress />
                        </Box>
                    ) : dispatches.length === 0 ? (
                        <Box sx={{ p: 3 }}>
                            <Subtle variant="body2">No email activity yet.</Subtle>
                        </Box>
                    ) : (
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>When</TableCell>
                                    <TableCell>Rule</TableCell>
                                    <TableCell>Recipient</TableCell>
                                    <TableCell>Status</TableCell>
                                    {!isMobile ? <TableCell>Detail</TableCell> : null}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {dispatches.map((row) => (
                                    <TableRow key={row.id}>
                                        <TableCell>{formatDate(row.created_at)}</TableCell>
                                        <TableCell>{row.rule_slug || (row.is_test ? "test" : "—")}</TableCell>
                                        <TableCell>{row.owner_email}</TableCell>
                                        <TableCell>
                                            <Chip
                                                size="small"
                                                label={row.status}
                                                color={statusColor(row.status)}
                                            />
                                        </TableCell>
                                        {!isMobile ? (
                                            <TableCell sx={{ maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis" }}>
                                                {row.error || row.subject || ""}
                                            </TableCell>
                                        ) : null}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </Paper>
                {dispatchPageCount > 1 ? (
                    <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                        <Pagination
                            count={dispatchPageCount}
                            page={dispatchPage}
                            onChange={(_e, value) => setDispatchPage(value)}
                        />
                    </Box>
                ) : null}
            </TabPanel>

            <Snackbar
                open={Boolean(toast)}
                autoHideDuration={3000}
                onClose={() => setToast("")}
                message={toast}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            />
        </PageContainer>
    );
}
